/**
 * Warm Just Sold — Stone & Bronze
 *
 * Archetype: the agent-brand flyer rather than the property flyer. A large
 * exterior sits behind an overlapping interior inset, which is what gives the
 * page depth without any shadow trickery, and the bronze bar at the foot turns
 * the whole sheet into a call to action.
 */

import { CANVAS, SPACE, TYPE, RADIUS, RULE } from '../../system/tokens';
import { getPalette, scrim } from '../../system/palettes';
import { roles } from '../../system/typography';
import { grid } from '../../system/grid';
import { text, shape, rule, image, page } from '../../system/elements';
import { contactBar, statRow } from '../../system/blocks';
import { PHOTOS } from '../../system/photos';

const ID = 'tpl-flyer-real-estate-05';

export default {
  id: ID,
  meta: {
    name: 'Just Sold — Stone & Bronze',
    collection: 'signature',
    archetype: 'overlapping-inset',
    documentType: 'Flyer',
    industry: 'Real Estate',
    subCategory: 'Real Estate',
    canvas: CANVAS.usLetterPortrait,
    palette: 'stone-bronze',
    pairing: 'dmserif-manrope',
    tags: ['real estate', 'flyer', 'just sold', 'agent', 'warm', 'bronze', 'farming', 'us letter'],
    description: 'Agent-brand "just sold" flyer: overlapping exterior and interior photographs on warm stone, closed by a bronze call-to-action bar.',
  },

  build() {
    const canvas = CANVAS.usLetterPortrait;
    const p = getPalette('stone-bronze');
    const r = roles('dmserif-manrope');
    const g = grid(canvas);
    const els = [];

    // ── Sold banner ─────────────────────────────────────────────────────────
    const BANNER_H = 220;
    els.push(shape(`${ID}-banner`, { x: 0, y: 0, width: canvas.width, height: BANNER_H, fill: p.ink }));
    els.push(text(`${ID}-banner-copy`, r.cta, 'Just Sold  ·  Represented the Seller', {
      x: g.left, y: (BANNER_H - TYPE.body * 1.4) / 2, width: g.contentWidth, align: 'center',
    }, { color: p.surface }));

    // ── Photographs: large exterior, overlapping interior inset ─────────────
    const HERO_Y = BANNER_H + SPACE.lg;
    const HERO_H = 1240;
    els.push(image(`${ID}-hero`, {
      x: g.left, y: HERO_Y, width: g.contentWidth, height: HERO_H,
      src: PHOTOS.terracottaVilla, cornerRadius: RADIUS.md,
    }));

    const INSET_W = 760;
    const INSET_H = 560;
    const insetX = g.right - INSET_W;
    const insetY = HERO_Y + HERO_H - INSET_H / 2;

    // Stone card behind the inset so it reads as lifted off the exterior shot.
    els.push(shape(`${ID}-inset-card`, {
      x: insetX - SPACE.xxs, y: insetY - SPACE.xxs,
      width: INSET_W + SPACE.xxs * 2, height: INSET_H + SPACE.xxs * 2,
      fill: p.bg, cornerRadius: RADIUS.md,
    }));
    els.push(image(`${ID}-inset`, {
      x: insetX, y: insetY, width: INSET_W, height: INSET_H,
      src: PHOTOS.warmNeutralInterior, cornerRadius: RADIUS.sm,
    }));

    // ── Copy block, set left of the inset ───────────────────────────────────
    let y = insetY + SPACE.lg;
    const COL = insetX - g.left - SPACE.lg;

    const eyebrow = text(`${ID}-eyebrow`, r.eyebrow, 'Sold in 9 days', { x: g.left, y, width: COL }, { color: p.accent });
    els.push(eyebrow);
    y += eyebrow.height + SPACE.sm;

    const headline = text(`${ID}-headline`, r.h1, '1802 Marchmont\nAvenue', { x: g.left, y, width: COL },
      { color: p.ink, fontSize: 145 });
    els.push(headline);
    y += headline.height + SPACE.sm;

    const sub = text(`${ID}-sub`, r.body, 'Scottsdale, Arizona 85251', { x: g.left, y, width: COL }, { color: p.inkMuted });
    els.push(sub);
    y = Math.max(y + sub.height, insetY + INSET_H) + SPACE.xl;

    // ── Result figures ──────────────────────────────────────────────────────
    els.push(rule(`${ID}-stats-rule`, { x: g.left, y, width: g.contentWidth, weight: RULE.hair, color: scrim(p.ink, 0.28) }));
    y += SPACE.sm;

    const stats = statRow(ID, r, {
      x: g.left, y, width: g.contentWidth,
      stats: [
        { value: '$1.42M', label: 'Sale price' },
        { value: '104%', label: 'Of asking' },
        { value: '9', label: 'Days on market' },
      ],
      valueColor: p.accent, labelColor: p.inkMuted, dividerColor: scrim(p.ink, 0.22),
    });
    els.push(...stats.elements);
    y = stats.y + SPACE.lg;

    const pitch = text(`${ID}-pitch`, r.body,
      'Thinking of selling on Marchmont? I know what this street is worth\nright now — and what it takes to get there. Ask for a free valuation.', {
        x: g.left, y, width: g.contentWidth,
      }, { color: p.ink });
    els.push(pitch);

    // ── Bronze call-to-action bar, pinned to the foot ───────────────────────
    const BAR_H = 240;
    const bar = contactBar(ID, r, {
      x: 0, y: canvas.height - BAR_H, width: canvas.width, height: BAR_H,
      copy: 'Marielle Vance  ·  (480) 555-0173  ·  harbourvine.com',
      fill: p.accent, inkColor: p.accentInk,
    });
    els.push(...bar.elements);

    return {
      canvas,
      pages: [page(`${ID}-page-1`, { name: 'Flyer', background: p.bg, elements: els })],
    };
  },
};
